import {
  AcademicCapIcon,
  AdjustmentsIcon,
  ArrowsExpandIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CameraIcon,
  ChatAlt2Icon,
  ChatAltIcon,
  CodeIcon,
  DocumentTextIcon,
  DotsHorizontalIcon,
  EmojiHappyIcon,
  FilmIcon,
  HashtagIcon,
  HeartIcon,
  HomeIcon,
  IdentificationIcon,
  MailIcon,
  MicrophoneIcon,
  MusicNoteIcon,
  PencilAltIcon,
  PhotographIcon,
  PuzzleIcon,
  ScaleIcon,
  ScissorsIcon,
  SparklesIcon,
  SpeakerphoneIcon,
  StatusOnlineIcon,
  SwitchHorizontalIcon,
  TranslateIcon,
  UserCircleIcon,
  UserGroupIcon,
  VideoCameraIcon,
  ViewGridAddIcon,
  VolumeUpIcon,
} from '@heroicons/react/outline';
import { ComponentType } from 'react';

import {
  getPresetToolHref,
  Preset,
  presetCategories,
  PresetCategory,
  presets,
} from '@/constants/presets';

export type IconComponent = ComponentType<{ className?: string }>;

/**
 * Catalogue strings in the page's language, picked from `tools.json` in
 * getStaticProps (`@/lib/presetCatalogText`). The page shows only titles,
 * descriptions and category names, about half of that namespace, so the
 * namespace itself is not loaded on /presets.
 */
export interface CatalogText {
  /** English title → [title, description]; '' keeps the English text. */
  tools: Record<string, [string, string]>;
  /** English category name → translated name. */
  categories: Record<string, string>;
  /** The page title, as the old page had it: `Tools` from `tools.json`. */
  pageTitle: string;
}

export const EMPTY_CATALOG_TEXT: CatalogText = {
  tools: {},
  categories: {},
  pageTitle: 'Tools',
};

/** Presets the catalogue lists (the dashboard-hidden ones stay out). */
export const catalogPresets = presets.filter((preset) => !preset.hideDashoard);

/** One colour and icon per category; tool icons take their first category's. */
export const CATEGORY_META: Record<
  PresetCategory,
  { slug: string; icon: IconComponent; tint: string; ink: string }
> = {
  Writing: {
    slug: 'writing',
    icon: PencilAltIcon,
    tint: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300',
    ink: 'text-indigo-600 dark:text-indigo-300',
  },
  Content: {
    slug: 'content',
    icon: DocumentTextIcon,
    tint: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300',
    ink: 'text-sky-600 dark:text-sky-300',
  },
  'Social Media': {
    slug: 'social-media',
    icon: HashtagIcon,
    tint: 'bg-pink-50 text-pink-600 dark:bg-pink-500/15 dark:text-pink-300',
    ink: 'text-pink-600 dark:text-pink-300',
  },
  Marketing: {
    slug: 'marketing',
    icon: SpeakerphoneIcon,
    tint: 'bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300',
    ink: 'text-orange-600 dark:text-orange-300',
  },
  'Email & Messages': {
    slug: 'email-messages',
    icon: MailIcon,
    tint: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
    ink: 'text-blue-600 dark:text-blue-300',
  },
  Business: {
    slug: 'business',
    icon: BriefcaseIcon,
    tint: 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-200',
    ink: 'text-slate-600 dark:text-slate-300',
  },
  HR: {
    slug: 'hr',
    icon: UserGroupIcon,
    tint: 'bg-teal-50 text-teal-600 dark:bg-teal-500/15 dark:text-teal-300',
    ink: 'text-teal-600 dark:text-teal-300',
  },
  Career: {
    slug: 'career',
    icon: IdentificationIcon,
    tint: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
    ink: 'text-emerald-600 dark:text-emerald-300',
  },
  Education: {
    slug: 'education',
    icon: AcademicCapIcon,
    tint: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    ink: 'text-amber-700 dark:text-amber-300',
  },
  'Images & Video': {
    slug: 'images-video',
    icon: PhotographIcon,
    tint: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
    ink: 'text-violet-600 dark:text-violet-300',
  },
  'Audio & Music': {
    slug: 'audio-music',
    icon: MusicNoteIcon,
    tint: 'bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-500/15 dark:text-fuchsia-300',
    ink: 'text-fuchsia-600 dark:text-fuchsia-300',
  },
  'Creative Writing': {
    slug: 'creative-writing',
    icon: BookOpenIcon,
    tint: 'bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-300',
    ink: 'text-purple-600 dark:text-purple-300',
  },
  'Health & Wellness': {
    slug: 'health-wellness',
    icon: HeartIcon,
    tint: 'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300',
    ink: 'text-rose-600 dark:text-rose-300',
  },
  Legal: {
    slug: 'legal',
    icon: ScaleIcon,
    tint: 'bg-stone-100 text-stone-600 dark:bg-stone-400/15 dark:text-stone-300',
    ink: 'text-stone-600 dark:text-stone-300',
  },
  'Code & Data': {
    slug: 'code-data',
    icon: CodeIcon,
    tint: 'bg-lime-50 text-lime-700 dark:bg-lime-500/15 dark:text-lime-300',
    ink: 'text-lime-700 dark:text-lime-300',
  },
  'Personal & Fun': {
    slug: 'personal-fun',
    icon: EmojiHappyIcon,
    tint: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300',
    ink: 'text-yellow-700 dark:text-yellow-300',
  },
  Other: {
    slug: 'other',
    icon: DotsHorizontalIcon,
    tint: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-slate-300',
    ink: 'text-gray-600 dark:text-slate-300',
  },
};

/** Categories with at least one listed tool, in the catalogue's order. */
export const CATALOG_CATEGORIES = presetCategories.filter((category) =>
  catalogPresets.some((preset) => categoriesOf(preset).includes(category)),
);

export function categoryBySlug(slug: unknown): PresetCategory | null {
  if (typeof slug !== 'string') return null;
  return (
    CATALOG_CATEGORIES.find(
      (category) => CATEGORY_META[category].slug === slug,
    ) ?? null
  );
}

function categoriesOf(preset: Pick<Preset, 'category'>): PresetCategory[] {
  return Array.isArray(preset.category)
    ? [...preset.category]
    : [preset.category as PresetCategory];
}

/**
 * The image, video, audio and agent products, in the order people opened
 * them from /presets (next pageview after /presets, 28 days to 2026-10-21:
 * AI Images 1,664 people, AI Videos 507, Marky 319, Image Editor 181,
 * Talking Videos 120, Interior 116, Agents 98, Photo Studio 96, Music 94,
 * Text to Speech 86, Workflows 75, Transcription 69, Video Agent 47, then
 * fewer than 40 each). Labels are the sidebar's (`dashboard` namespace);
 * colours match the dashboard's phone icons.
 */
export interface CatalogApp {
  /** Favorites id: the slug of the sidebar pin for the same page. */
  slug: string;
  /** The catalogue preset this app stands for; it is shown as the app. */
  presetSlug?: string;
  /** In the `dashboard` namespace. */
  label: string;
  /** In the `tools_page` namespace. */
  desc: string;
  href: string;
  /** For signed-out visitors when the app has no preset with a landing page. */
  guestHref?: string;
  icon: IconComponent;
  tint: string;
  isNew?: boolean;
}

export const APPS: CatalogApp[] = [
  {
    slug: 'ai-images',
    presetSlug: 'ai-images',
    label: 'AI Images',
    desc: 'Create and edit images',
    href: '/ai-images',
    icon: PhotographIcon,
    tint: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
  },
  {
    slug: 'ai-videos',
    presetSlug: '/ai-videos',
    label: 'AI Videos',
    desc: 'Text or image to video',
    href: '/ai-videos',
    icon: VideoCameraIcon,
    tint: 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300',
  },
  {
    slug: 'chat',
    presetSlug: 'chat',
    label: 'Marky Agent',
    desc: 'Your AI agent for any task',
    href: '/chat',
    icon: ChatAlt2Icon,
    tint: 'bg-primary-100 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300',
  },
  {
    slug: 'ai-image-editor',
    presetSlug: 'ai-image-editor',
    label: 'AI Image Editor',
    desc: 'Edit images with a prompt',
    href: '/ai-image-editor',
    icon: AdjustmentsIcon,
    tint: 'bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300',
  },
  {
    slug: 'ai-talking-videos',
    presetSlug: '/ai-talking-videos',
    label: 'AI Talking Videos',
    desc: 'Make any photo speak',
    href: '/ai-talking-videos',
    icon: UserCircleIcon,
    tint: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300',
  },
  {
    slug: 'ai-interior-designer',
    presetSlug: 'ai-interior-designer',
    label: 'AI Interior Designer',
    desc: 'Redesign any room',
    href: '/ai-interior-designer',
    icon: HomeIcon,
    tint: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  {
    slug: 'bots',
    presetSlug: 'bots',
    label: 'Bots',
    desc: 'Answers customers 24/7',
    href: '/agents',
    guestHref: '/chatbots',
    icon: ChatAltIcon,
    tint: 'bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-300',
  },
  {
    slug: 'ai-images-models',
    presetSlug: '/ai-images/models',
    label: 'AI Photo Studio',
    desc: 'Train AI on your own photos',
    href: '/ai-images/models',
    icon: CameraIcon,
    tint: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
  },
  {
    slug: 'music-generator',
    presetSlug: 'music-generator',
    label: 'AI Music Generator',
    desc: 'Full songs from a prompt',
    href: '/music-generator',
    icon: MusicNoteIcon,
    tint: 'bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-500/15 dark:text-fuchsia-300',
  },
  {
    slug: 'tts',
    presetSlug: 'tts',
    label: 'AI Text to Speech',
    desc: '1,900+ voices',
    href: '/tts',
    icon: VolumeUpIcon,
    tint: 'bg-teal-100 text-teal-600 dark:bg-teal-500/15 dark:text-teal-300',
  },
  {
    slug: 'workflows',
    label: 'Workflows',
    desc: 'Multi-step AI workflows',
    href: '/workflows',
    guestHref: '/ai-workflows',
    icon: ViewGridAddIcon,
    tint: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  },
  {
    slug: 'audios',
    presetSlug: 'audios',
    label: 'AI Transcription',
    desc: 'Transcripts and summaries',
    href: '/audios',
    icon: MicrophoneIcon,
    tint: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  },
  {
    slug: 'video-agent',
    presetSlug: 'video-agent',
    label: 'Video Agent',
    desc: 'One sentence in, video out',
    href: '/create-video',
    icon: FilmIcon,
    tint: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300',
    isNew: true,
  },
  {
    slug: 'note-taker',
    presetSlug: 'note-taker',
    label: 'Record & Transcribe',
    desc: 'Notes from any conversation',
    href: '/note-taker',
    icon: StatusOnlineIcon,
    tint: 'bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300',
  },
  {
    slug: 'ai-video-editor',
    presetSlug: 'ai-video-editor',
    label: 'AI Video Editor',
    desc: 'Edit and caption videos',
    href: '/ai-video-editor',
    icon: ScissorsIcon,
    tint: 'bg-pink-100 text-pink-600 dark:bg-pink-500/15 dark:text-pink-300',
  },
  {
    slug: 'voice-changer',
    presetSlug: 'voice-changer',
    label: 'AI Voice Changer',
    desc: 'Speak in any voice',
    href: '/voice-changer',
    icon: SwitchHorizontalIcon,
    tint: 'bg-purple-100 text-purple-600 dark:bg-purple-500/15 dark:text-purple-300',
    isNew: true,
  },
  {
    slug: 'sprite-generator',
    presetSlug: 'sprite-generator',
    label: 'Sprite Generator',
    desc: 'Sprite sheets for 2D games',
    href: '/sprite-generator',
    icon: PuzzleIcon,
    tint: 'bg-lime-100 text-lime-700 dark:bg-lime-500/15 dark:text-lime-300',
  },
  {
    slug: 'magic-upscaler',
    presetSlug: 'magic-upscaler',
    label: 'Magic Upscaler',
    desc: 'Sharpen and enlarge images',
    href: '/magic-upscaler',
    icon: ArrowsExpandIcon,
    tint: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300',
  },
  {
    slug: 'headshots',
    presetSlug: 'headshots',
    label: 'Headshots',
    desc: 'Studio-quality headshots',
    href: '/headshots',
    icon: IdentificationIcon,
    tint: 'bg-slate-200/70 text-slate-700 dark:bg-white/10 dark:text-slate-200',
  },
  {
    slug: 'dubbing',
    presetSlug: 'dubbing',
    label: 'AI Dubbing',
    desc: 'Dub videos in 30+ languages',
    href: '/dubbing',
    icon: TranslateIcon,
    tint: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
  },
  {
    slug: 'audio-generator',
    presetSlug: 'audio-generator',
    label: 'AI Audio Generator',
    desc: 'Sound, voice and music',
    href: '/audio-generator',
    icon: SpeakerphoneIcon,
    tint: 'bg-teal-100 text-teal-600 dark:bg-teal-500/15 dark:text-teal-300',
    isNew: true,
  },
  {
    slug: 'sound-effects',
    presetSlug: 'sound-effects',
    label: 'AI Sound Effects',
    desc: 'Sound effects from a prompt',
    href: '/sound-effects',
    icon: VolumeUpIcon,
    tint: 'bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-300',
  },
  {
    slug: 'voices',
    presetSlug: 'voices',
    label: 'Brand Voice',
    desc: 'Write in your own voice',
    href: '/voices',
    icon: SpeakerphoneIcon,
    tint: 'bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300',
  },
  {
    slug: 'documents',
    presetSlug: 'documents',
    label: 'Long-form editor',
    desc: 'Write long documents with AI',
    href: '/documents',
    icon: DocumentTextIcon,
    tint: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  },
];

/** Apps shown before "Show all" (two rows of four, and of phone icons). */
export const APPS_FIRST = 8;

/**
 * Text tools by use: people who opened the tool from /presets, plus a fiftieth
 * of everyone who opened it from anywhere (sign-ups from the template landing
 * pages dominate that count), 28 days to 2026-10-21 in PostHog. The first
 * nine are "Popular tools"; the list also orders each category. Refresh it
 * now and then (recipe in docs/notes/presets-templates.md).
 */
// prettier-ignore
export const TOOL_RANK = [
  'custom-generator', 'pick-up-line-generator', 'progress-note-generator',
  'fanfiction-maker', 'email-generation', 'content-rewriter',
  'worksheet-generator', 'accounting-solver', 'paragraph-writer',
  'reply-to-email', 'writing-enhancer', 'headline-generator',
  'humanize-ai-text', 'soap-note-generator', 'facebook-post-generator',
  'riddle-solver', 'sermon-generator', 'ai-text-generator',
  'instagram-post-caption', 'song-lyrics-generator',
  'chatgpt-prompt-generator', 'reply-to-messsage', 'hook-generator',
  'lottery-number-generator', 'blog-post-v3', 'riddle-generator',
  'dap-note-generator', 'letter-generator', 'clickbait-title-generator',
  'news-article-generator', 'email-rewriter', 'sentence-generator',
  'video-prompt-generator', 'treatment-plan-generator',
  'image-description-generator', 'performance-review-generator',
  'social-stories-generator', 'ai-story-generator',
  'chatgpt-persona-instructions-generator', 'content-summarizer',
  'birp-note-generator', 'text-message-generator', 'sentence-rewriter',
  'linkedin-post-generator', 'pdf-summarizer', 'marketing-campaign-generator',
  'blog-post-v2', 'smart-goal-generator', 'romance-story-generator',
  'economics-solver', 'professional-bio-generator',
  'character-backstory-generator', 'seo-title-meta-descriptions',
  'paraphrasing-tool', 'meeting-minutes-generator',
  'presentation-slides-text-generator', 'native-speaker',
  'youtube-video-to-article', 'youtube-video-ideas',
  'resume-headline-generator', 'powerball-number-generator',
  'character-name-generator', 'diss-track-generator', 'rap-lyrics-generator',
  'love-letter-generator', 'prayer-generator', 'tinder-response',
  'ship-name-generator', 'speech-writer', 'ai-joke-generator',
  'birthday-card-creator', 'rhyme-generator', 'rubric-generator',
  'band-name-generator', 'podcast-script-generator', 'manuscript-writer',
  'movie-recommender', 'greetings-generator', 'text-simplifier',
  'real-estate-listing-generator', 'book-title-generator',
  'debate-argument-generator', 'dj-name-generator',
  'google-sheets-excel-formula-generator', 'synopsis-generator',
  'affirmation-generator', 'mega-millions-number-generator', 'roast-bot',
  'text-merger', 'lesson-plan-generator', 'ghostwriter', 'chemistry-assistant',
  'aba-notes-generator', 'introduction-paragraph-generator',
  'birthday-invitation-generator', 'chapter-generator', 'art-prompt-generator',
  'character-description-generator', 'statistics-solver',
  'research-problem-statement-generator', 'interview-answers-generator',
  'meal-plan-generator', 'research-paper-generator', 'dialogue-generator',
  'letter-of-recommendation-generator', 'apology-generator',
  'social-media-post-generator', 'event-name-generator', 'monologue-generator',
  'book-summarizer', 'thank-you-note-generator', 'iep-goal-generator',
  'poem-generator', 'grant-proposal-generator', 'story-plot-generator',
  'song-idea-generator', 'announcement-generator',
  'discussion-board-response-generator', 'workout-plan-generator',
  'biology-solver', 'cornell-notes-generator', 'event-planner',
  'staff-schedule-generator', 'bullet-points-generator', 'content-shortener',
  'legal-document-generator', 'paragraph-expander', 'keywords-generator',
  'passion-project-generator', 'therapy-intake', 'grammar-corrector',
  'quora-answers', 'lullaby-generator', 'bedtime-story-generator',
  'reference-letter-generator', 'universal-translator',
  'content-idea-generator', 'idea-generator', 'feedback-generator',
  'project-report-generator', 'linkedin-bio-generator',
  'instagram-carousel-generator', 'khutbah-generator', 'reel-script-generator',
  'contract-generator', 'ask-ai-writer', 'letter-of-request-generator',
  'book-outline-generator', 'discussion-post-commenter', 'brainstorming-tool',
  'eli5', 'sentence-expander', 'metaphor-creator', 'sop-generator',
  'quiz-answer-assistant', 'mission-statement-generator',
  'cover-letter-generator', 'fill-the-gaps', 'musician-bio-generator',
  'team-name-generator', 'reflection-writer', 'youtube-video-title',
  'policy-generator', 'email-subject-line-generation',
  'write-like-shakespeare', 'physics-solver', 'sentence-finisher',
  'resume-skills-generator', 'etsy-product-description',
  'character-reference-letter-generator', 'demand-letter-generator',
  'resume-summary-generator', 'conclusion-generator',
  'press-release-generator', 'haiku-generator', 'podcast-name-generator',
  'resume-bullet-point-generator', 'hypothesis-maker',
  'instagram-reel-ideas-generator', 'syllabus-generator',
  'album-title-generator', 'job-description-generator',
  'advertisement-copy-generator', 'catchy-tagline',
  'marketing-strategy-generator', 'job-offer-letter-generator', 'tinder-bio',
  'quiz-generator', 'blog-post', 'recipe-generator', 'about-me-generator',
  'elevator-pitch-generator', 'youtube-video-description', 'study-guide-maker',
  'clinical-summary-update-generator', 'podcast-episode-description-generator',
  'resume-objective-generator', 'essay-generator-with-references',
  'pun-generator', 'personal-statement-generator', 'screenplay-generator',
  'business-plan-generator', 'meta-description-generator',
];

export const POPULAR_COUNT = 9;

/** A text tool counts as new for this long after its `dateAdded`. */
const NEW_FOR_DAYS = 120;

export interface CatalogEntry {
  /** Unique: two presets share the `music-generator` slug. */
  id: string;
  /** Favorites id. */
  slug: string;
  /** Where signed-in people go. */
  href: string;
  /** Where signed-out visitors go: the public landing page when there is one. */
  guestHref: string;
  name: string;
  description: string;
  categories: PresetCategory[];
  icon: IconComponent;
  tint: string;
  app?: CatalogApp;
  isCustom?: boolean;
  /** Shared with the user's team (custom tools only). */
  isTeam?: boolean;
  isNew?: boolean;
  /**
   * Position in TOOL_RANK (lower is used more; apps are negative so they
   * lead their categories); unranked tools sort last.
   */
  rank: number;
  /** Normalised text for search. */
  search: {
    name: string;
    english: string;
    keywords: string;
    description: string;
    categories: string;
  };
}

/** Lower-case, accents and punctuation stripped, for search matching. */
export function normalize(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[&/\\|,.:;!?()[\]{}"'’`_+\-–—]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const rankOf = (() => {
  const ranks = new Map(TOOL_RANK.map((slug, index) => [slug, index]));
  return (slug: string) => ranks.get(slug) ?? TOOL_RANK.length;
})();

function isRecent(dateAdded?: string) {
  if (!dateAdded) return false;
  const added = Date.parse(dateAdded);
  return (
    Number.isFinite(added) &&
    Date.now() - added < NEW_FOR_DAYS * 24 * 60 * 60 * 1000
  );
}

/**
 * The page's catalogue: every listed preset (apps shown under their sidebar
 * name) plus the apps that have no preset. `translate` resolves an app's
 * label in the `dashboard` namespace.
 */
export function buildCatalog(
  text: CatalogText,
  translate: (key: string) => string,
  categoryName: (category: PresetCategory) => string,
): CatalogEntry[] {
  const appsByPreset = new Map(
    APPS.filter((app) => app.presetSlug).map((app) => [app.presetSlug, app]),
  );
  const entries: CatalogEntry[] = catalogPresets.map((preset, index) => {
    const app = appsByPreset.get(preset.slug);
    // Only the first preset per app stands for it (`music-generator` is
    // listed twice: as the music and as the song generator).
    if (app) appsByPreset.delete(preset.slug);
    const [title, description] = text.tools[preset.title] ?? ['', ''];
    const categories = categoriesOf(preset);
    const href = app?.href ?? getPresetToolHref(preset);
    const guestHref =
      app?.guestHref ??
      (preset.isAbsoluteURL && preset.landingPageSlug
        ? preset.landingPageSlug.startsWith('/')
          ? preset.landingPageSlug
          : `/${preset.landingPageSlug}`
        : href);
    const name = app ? translate(app.label) : title || preset.title;
    const shownDescription = description || preset.description;
    return {
      id: `${preset.slug}#${index}`,
      slug: app?.slug ?? preset.slug,
      href,
      guestHref,
      name,
      description: shownDescription,
      categories,
      icon: app?.icon ?? CATEGORY_META[categories[0]].icon,
      tint: app?.tint ?? CATEGORY_META[categories[0]].tint,
      app,
      isNew: app ? app.isNew : isRecent(preset.dateAdded),
      // Apps lead their categories, in the order of APPS.
      rank: app ? APPS.indexOf(app) - APPS.length : rankOf(preset.slug),
      search: {
        name: normalize(name),
        english: normalize(app ? `${app.label} ${preset.title}` : preset.title),
        keywords: normalize((preset.searchKeywords ?? []).join(' ')),
        description: normalize(shownDescription),
        categories: normalize(
          categories
            .map((category) => `${category} ${categoryName(category)}`)
            .join(' '),
        ),
      },
    };
  });

  // Apps without a preset of their own (Workflows).
  for (const app of APPS) {
    if (app.presetSlug) continue;
    const name = translate(app.label);
    entries.push({
      id: app.slug,
      slug: app.slug,
      href: app.href,
      guestHref: app.guestHref ?? app.href,
      name,
      description: '',
      categories: [],
      icon: app.icon,
      tint: app.tint,
      app,
      isNew: app.isNew,
      rank: APPS.indexOf(app) - APPS.length,
      search: {
        name: normalize(name),
        english: normalize(app.label),
        keywords: '',
        description: '',
        categories: '',
      },
    });
  }
  return entries;
}

/** A user's own or team template, from /api/templates/list. */
export function customEntry(
  preset: Pick<Preset, 'slug' | 'title' | 'description'>,
  isTeam: boolean,
): CatalogEntry {
  const name = preset.title || preset.slug;
  const href = `/presets/${preset.slug}`;
  return {
    id: `custom:${preset.slug}`,
    slug: preset.slug,
    href,
    guestHref: href,
    name,
    description: preset.description ?? '',
    categories: [],
    icon: SparklesIcon,
    tint: 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300',
    isCustom: true,
    isTeam,
    rank: -1,
    search: {
      name: normalize(name),
      english: '',
      keywords: '',
      description: normalize(preset.description ?? ''),
      categories: '',
    },
  };
}

export const byRank = (a: CatalogEntry, b: CatalogEntry) => a.rank - b.rank;

/** Categories made mostly of apps; elsewhere the apps' broad tags add noise. */
const APP_CATEGORIES = new Set<PresetCategory>([
  'Images & Video',
  'Audio & Music',
]);

/**
 * A category's entries in the order its view lists them: by use, with the
 * apps (already in their own section) after the text tools, except in the
 * image/video and audio categories, which are mostly apps.
 */
export function categoryEntries(
  entries: CatalogEntry[],
  category: PresetCategory,
): CatalogEntry[] {
  const inside = entries
    .filter((entry) => entry.categories.includes(category))
    .sort(byRank);
  if (APP_CATEGORIES.has(category)) return inside;
  return [
    ...inside.filter((entry) => !entry.app),
    ...inside.filter((entry) => entry.app),
  ];
}

/** Non-Latin text (CJK, Thai, Arabic…) has no spaces to mark word starts. */
const NON_LATIN = /[^\u0000-ɏ\s]/;

function fieldScore(text: string, term: string, weight: number) {
  if (!text) return 0;
  if (text.startsWith(term)) return weight;
  if (text.includes(` ${term}`)) return weight * 0.8;
  if ((term.length >= 3 || NON_LATIN.test(term)) && text.includes(term)) {
    return weight * 0.45;
  }
  return 0;
}

/** True when `word` starts like `term` with one typo (two for long terms). */
function nearMatch(word: string, term: string) {
  if (term.length < 4 || NON_LATIN.test(term)) return false;
  const target = word.slice(0, term.length + 1);
  const limit = term.length >= 7 ? 2 : 1;
  // Levenshtein distance between `term` and any prefix of `target`.
  let previous = Array.from({ length: target.length + 1 }, (_, i) => i);
  for (let i = 1; i <= term.length; i++) {
    const current = [i];
    for (let j = 1; j <= target.length; j++) {
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + (term[i - 1] === target[j - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return Math.min(...previous.slice(term.length - 1)) <= limit;
}

/**
 * Ranks entries for a query. Every word of the query must match a name,
 * keyword, category or description (start of a word scores higher than the
 * middle); a near miss on a name word ("speach") still counts when nothing
 * matches exactly.
 */
export function searchCatalog(
  entries: CatalogEntry[],
  query: string,
): CatalogEntry[] {
  const terms = normalize(query).split(' ').filter(Boolean);
  if (!terms.length) return [];

  const score = (entry: CatalogEntry, fuzzy: boolean) => {
    let total = 0;
    for (const term of terms) {
      const { search } = entry;
      let best = Math.max(
        fieldScore(search.name, term, 100),
        fieldScore(search.english, term, 90),
        fieldScore(search.keywords, term, 70),
        fieldScore(search.categories, term, 35),
        fieldScore(search.description, term, 30),
      );
      if (!best && fuzzy) {
        const words = `${search.name} ${search.english}`.split(' ');
        if (words.some((word) => nearMatch(word, term))) best = 40;
      }
      if (!best) return 0;
      total += best;
    }
    // Among equal matches, apps and the more used tools first.
    return total + (entry.app ? 6 : 0) + Math.max(0, 5 - entry.rank / 40);
  };

  const rank = (fuzzy: boolean) =>
    entries
      .map((entry) => ({ entry, value: score(entry, fuzzy) }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value || a.entry.rank - b.entry.rank)
      .map((item) => item.entry);

  const exact = rank(false);
  return exact.length ? exact : rank(true);
}
